import { Spin } from 'antd';
import download from 'downloadjs';
import parser from 'html-react-parser';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { AiOutlineCloudDownload } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Apis from '../../../../../api';
import { HEADERMENU_PATH } from '../../../../../routes/RoutesURL';
import { RootState } from '../../../../../store/rootReducer';
import HSButton from '../../../../common/Button';
import Loader from '../../../../common/Loader';
import LoginActions from '../../../../login/redux/actions';
import { InvestorStatus, agreementsResign } from '../../../../login/utils/Status';
import MessageActions from '../../../../message/redux/actions';
import { ONBOARDING_PATH } from '../../Onbording';
import OnboardingHeading from '../OnboardingHeading';
import OnboardingCard from '../common/Card';
import OnboardingRightContainer from '../common/Container';
import NDAConfirm from '../common/NDAConfirm';
import NotificationBanner from '../common/NotificationBanner';

type Props = {
    isResign?: boolean;
};

const NDA: React.FC<Props> = ({ isResign }) => {
    const uuid = useSelector((state: RootState) => state.user.uuid);
    const user = useSelector((state: RootState) => state.user);
    const investor = useSelector((state: RootState) => state.user.investor);
    const agreements = useSelector((state: RootState) => state.user.investor?.agreements);
    const status = investor?.status;
    const NDA = agreements?.find((a) => a.agreementType === 'NDA');
    const resignNDA = NDA?.shouldResign;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [NDAHtml, setNDAHtml] = useState<string | undefined>(undefined);
    const [loader, setLoader] = useState(false);
    const [isDisableAgree, setIsDisableAgree] = useState(true);
    const [loaderAgree, setLoaderAgree] = useState(false);

    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [agreementUUID, setAgreementUUID] = useState<string | undefined>(undefined);

    const getNDAHTMLReq = useCallback(() => {
        if (uuid) {
            setLoader(true);
            Apis.getNDAData(uuid)
                .then(({ data }) => {
                    setNDAHtml(data.html);
                    setAgreementUUID(data.agreementUUID);
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setLoader(false);
                });
        }
        return () => {};
    }, [uuid, dispatch]);

    useEffect(() => {
        if (resignNDA || !NDA || status === InvestorStatus.PendingNDA) {
            getNDAHTMLReq();
        }
    }, [getNDAHTMLReq, NDA, resignNDA, status]);

    useEffect(() => {
        if (isResign && !resignNDA) {
            navigate(HEADERMENU_PATH.deals);
        }
    }, [isResign, navigate, resignNDA]);

    const onscroll = (event: any) => {
        const bottom = event.target.scrollHeight - event.target.scrollTop - 50 <= event.target.clientHeight;
        if (bottom) {
            setIsDisableAgree(false);
        }
    };

    const onClickIAgreeOrSkip = (flag: boolean) => {
        if (uuid) {
            setLoaderAgree(true);
            Apis.confirmNDA(uuid, {
                agreementType: 'NDA',
                skip: flag,
                agreementUUID: agreementUUID,
            })
                .then(({ data }) => {
                    dispatch(
                        LoginActions.setUserData({
                            ...user,
                            currentStep: undefined,
                            investor: data,
                        }),
                    );
                    if (isResign) {
                        navigate(HEADERMENU_PATH.deals);
                    } else {
                        navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.INVESTMENT_PREFERENCES}`);
                    }
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setLoaderAgree(false);
                    setAgreementUUID(undefined);
                });
        }
    };

    if (NDA?.lastAudit?.at && investor?.status !== InvestorStatus.PendingNDA && !isResign) {
        if (!resignNDA) {
            return <NDAReadView />;
        }
    }

    return (
        <div id="nda">
            {resignNDA && NDA?.lastAudit?.at && (
                <div className="my-3 mb-5">
                    <NotificationBanner desc={agreementsResign} />
                </div>
            )}

            <NDAConfirm
                open={openConfirmation}
                setOpen={setOpenConfirmation}
                onConfirm={() => {
                    setOpenConfirmation(false);
                    onClickIAgreeOrSkip(false);
                }}
            />
            <div className="flex justify-center">
                <div className="w-full flex justify-center items-center" style={isResign ? { width: '100%' } : {}}>
                    {loader && <Loader />}
                    {!loader && (
                        <div>
                            <OnboardingHeading title="Non-Disclosure Agreement" infoText="NDA" />

                            <OnboardingCard className="flex-col shadow-none" style={{ boxShadow: 'none' }}>
                                <div
                                    className="h-96 overflow-auto "
                                    onScrollCapture={onscroll}
                                    style={isResign ? { minHeight: '60vh', maxHeight: '60vh', height: 'auto' } : {}}
                                >
                                    {NDAHtml && <div className="p-4 bg-white">{parser(NDAHtml)}</div>}
                                </div>
                                <div className="flex justify-center items-center my-6 w-full mb-2">
                                    <HSButton
                                        title="I Agree"
                                        disabled={isDisableAgree}
                                        loading={loaderAgree}
                                        onClick={() => setOpenConfirmation(true)}
                                        type="primary"
                                    />
                                    {investor && investor.skipNDA && (
                                        <HSButton
                                            title="Skip this step"
                                            light
                                            className="ml-2 onboarding_btn"
                                            onClick={() => onClickIAgreeOrSkip(true)}
                                        />
                                    )}
                                </div>
                            </OnboardingCard>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NDA;

const NDAReadView = () => {
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);

    const investorUUID = useSelector((state: RootState) => state.user.investor?.uuid);
    const agreements = useSelector((state: RootState) => state.user.investor?.agreements);
    const NDA = agreements?.find((a) => a.agreementType === 'NDA');
    const ndaSignDate = NDA?.lastAudit.at ? moment(NDA?.lastAudit.at).format('YYYY-MM-DD') : undefined;
    const ndaVenue = NDA?.venue;
    const ndaContent = NDA?.file;
    const agreementUUID = NDA?.uuid;

    const downloadFile = (type: string) => {
        if (investorUUID && agreementUUID) {
            setLoader(true);
            Apis.downloadFile(investorUUID, agreementUUID)
                .then((res) => {
                    download(new Blob([res.data]), `${type}.pdf`, 'application/pdf');
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const onClickNext = () => {
        navigate(`${HEADERMENU_PATH.onboarding}/${ONBOARDING_PATH.INVESTMENT_PREFERENCES}`);
    };

    return (
        <OnboardingRightContainer>
            <p className="page_title_onboarding mt-9">Non-Disclosure Agreement</p>
            <div className="w-full py-2 bg-white mt-3 px-4 rounded-lg flex justify-center items-center">
                <div className="flex items-center text-base Lato">
                    <div>
                        {ndaVenue !== 'Skipped' && `Signed`}
                        <b>&nbsp;{ndaVenue}</b> on <b>{ndaSignDate}</b>
                    </div>
                    {ndaContent && !loader && <DownloadButton onClick={() => downloadFile('NDA')} />}
                    {loader && <Spin className="ml-1 h-7 w-8 flex justify-center items-center" />}
                </div>
            </div>
            <div className="flex justify-center items-center mt-10 mb-5">
                <HSButton title="Next" onClick={onClickNext} type="primary" />
            </div>
        </OnboardingRightContainer>
    );
};

type DownloadProps = {
    onClick: () => void;
};

export const DownloadButton = ({ onClick }: DownloadProps) => {
    return (
        <div className="p-1 px-1.5 w-8 h-7 rounded-full cursor-pointer ml-1 " onClick={onClick}>
            <AiOutlineCloudDownload size="1.4rem" color="#13974B" />
        </div>
    );
};
